<template>
  <div :class="{ 'ml-5': !embedded }">
    <v-overlay :value="isLoading" fixed>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <h1 v-if="!embedded" class="text-h5 font-weight-bold">Agregar Mascota</h1>
    <h3 :class="embedded ? 'mt-2' : 'mt-10'">Datos de la mascota</h3>
    <v-form ref="form" v-model="valid" :disabled="isLoading">
      <!-- Primera fila -->
      <v-row class="my-1">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="pet.name"
            label="Nombre de la mascota"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="pet.sex"
            :items="sexOptions"
            label="Sexo"
            :rules="[rules.required]"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="birthdateMenu"
            v-model="birthdateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="pet.birthdate"
                prepend-icon="mdi-calendar"
                label="Fecha de nacimiento"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="[rules.required]"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="pet.birthdate"
              @input="birthdateMenu = false"
              locale="es"
              :min="'2004-01-01'"
              :max="new Date().toISOString().slice(0, 10)"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <!-- Segunda fila -->
      <v-row class="my-1">
        <v-col cols="12" md="3">
          <v-select
            v-model="pet.species"
            :items="speciesOptions"
            label="Especie"
            :rules="[rules.required]"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details="auto"
            @change="getBreeds"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="pet.breed"
            :items="breedOptions"
            label="Raza"
            :rules="[rules.breedRequired]"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Eliminamos el botón "Obtener Planes" -->

      <!-- Tercera fila -->
      <h3 class="mt-3">Datos de membresía</h3>
      <!-- Toggle tipo de membresía -->
      <v-row class="my-1">
        <v-col cols="auto" class="d-flex align-center" style="gap: 8px;">
          <span style="font-size: 0.85rem; color: rgba(0,0,0,0.6);">Tipo de membresía:</span>
          <span style="font-size: 0.85rem; color: rgba(0,0,0,0.6);">B2C</span>
          <v-switch
            v-model="membershipIsPlan"
            dense
            hide-details
            class="mt-0 pt-0"
            color="primary"
            inset
            @change="onMembershipTypeChange"
          ></v-switch>
          <span style="font-size: 0.85rem; color: rgba(0,0,0,0.6);">Plan (corporativo)</span>
        </v-col>
      </v-row>
      <!-- Contrato / Plan para tipo corporativo (desde UCA del usuario) -->
      <v-row class="my-1" v-if="membershipIsPlan">
        <v-col cols="12" md="5">
          <v-select
            v-model="contractSelected"
            :items="userContracts"
            label="Contrato"
            :rules="[rules.required]"
            item-text="displayName"
            item-value="contratoId"
            return-object
            :loading="loadingUserContracts"
            :no-data-text="loadingUserContracts ? 'Cargando...' : 'Sin contratos disponibles'"
            @change="onContractChange"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col cols="12" md="5">
          <v-select
            v-model="planSelected"
            :items="allowedPlanes"
            label="Plan"
            :rules="[rules.required]"
            item-text="planName"
            item-value="planId"
            return-object
            :disabled="!contractSelected"
            @change="onPlanChange"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="my-1">
        <v-col cols="12" md="4" v-if="!membershipIsPlan">
          <v-select
            v-model="membership.plan"
            :items="planOptions"
            label="Plan"
            :rules="[rules.required]"
            item-text="text"
            item-value="value"
            :loading="loadingPlans"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="membership.paymentMethod"
            :items="paymentMethodOptions"
            label="Modalidad de pago"
            :rules="[rules.required]"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="membership.coupon"
            :items="couponOptions"
            item-text="text"
            item-value="value"
            :loading="loadingCoupons"
            :active="canSelectCoupon"
            outlined
            dense
            hide-details="auto"
            label="Cupón"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="membership.acquisitionChannel"
            :items="acquisitionChannelOptions"
            :loading="loadingAcquisitionChannels || loadingAcquisitionChannelPreview"
            label="Canal de adquisición"
            hint="Vacío = se calcula automáticamente según el contrato"
            persistent-hint
            clearable
            outlined
            dense
            hide-details="auto"
          ></v-autocomplete>
        </v-col>
      </v-row>

      <!-- Cuarta fila -->
      <v-row class="my-1">
        <v-col cols="12" md="3">
          <v-menu
            ref="startDateMenu"
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="membership.startDate"
                prepend-icon="mdi-calendar"
                label="A contar de"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="[rules.required]"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="membership.startDate"
              @change="onStartDateChange"
              locale="es"
              :min="'2004-01-01'"
              :max="new Date().toISOString().slice(0, 10)"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="graceDateMenu"
            v-model="graceDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="membership.startGraceDays"
                prepend-icon="mdi-calendar"
                label="Fecha de carencia"
                hint="Por defecto la fecha de inicio; editable"
                persistent-hint
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="membership.startGraceDays"
              @input="graceDateMenu = false"
              locale="es"
              :min="'2004-01-01'"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="membership.status"
            :items="statusOptions"
            label="Estado membresía"
            :rules="[rules.required]"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Modal de Confirmación -->
      <v-dialog v-model="showConfirmationDialog" persistent max-width="500" @keydown.esc="showConfirmationDialog = false">
        <v-card>
          <v-card-title class="headline">
            Confirmación
          </v-card-title>
          <v-card-text>
            Esta acción creará una membresía con estado:
            <strong>{{ getMembershipStatusText }}</strong
            >. ¿Deseas continuar?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showConfirmationDialog = false">Cancelar</v-btn>
            <v-btn color="primary" @click="confirmSubmit">Aceptar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Botones -->
      <v-row class="mt-4">
        <v-col cols="12" class="text-right">
          <v-btn color="primary" @click="showConfirmation">Guardar</v-btn>
          <v-btn text @click="cancel">Cancelar</v-btn>
        </v-col>
      </v-row>

      <!-- Snackbar para mensajes de éxito -->
      <v-snackbar v-model="snackbarSuccess" :timeout="3000" color="green" top>
        {{ snackbarMessage }}
        <v-btn color="white" text @click="snackbarSuccess = false">Cerrar</v-btn>
      </v-snackbar>

      <!-- Snackbar para mensajes de error -->
      <v-snackbar v-model="snackbarError" :timeout="3000" color="red" top>
        {{ snackbarMessage }}
        <v-btn color="white" text @click="snackbarError = false">Cerrar</v-btn>
      </v-snackbar>
    </v-form>
  </div>
</template>

<script>
import PetsService from '../services/pets.service';
import PlansService from '../services/plans.service';
import CouponsService from '../services/coupons.service';
import MembershipService from '../services/memberships.service';
import UserService from '../services/users.service';

export default {
  props: {
    userId: { type: String, required: true },
    // Dentro de un diálogo (360): sin título de página; al guardar o cancelar avisa al
    // contenedor en vez de navegar.
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      isLoading: false,
      user: null,
      valid: false,
      canSelectCoupon: false,
      pet: {
        name: '',
        species: null,
        birthdate: '',
        sex: null,
        breed: null,
        age: { quantity: 0, unit: '' }
      },
      membership: {
        plan: null,
        paymentMethod: null,
        coupon: null,
        startDate: '',
        startGraceDays: '',
        status: null,
        ucaId: null,
        acquisitionChannel: null
      },
      acquisitionChannelOptions: [],
      loadingAcquisitionChannels: false,
      loadingAcquisitionChannelPreview: false,
      sexOptions: [
        { text: 'Macho', value: 'male' },
        { text: 'Hembra', value: 'female' }
      ],
      speciesOptions: [
        { text: 'Perro', value: 'dog' },
        { text: 'Gato', value: 'cat' }
      ],
      breedOptions: [],
      planOptions: [],
      paymentMethodOptions: [
        { text: 'Anual', value: 1 },
        { text: 'Mensual', value: 2 }
      ],
      couponOptions: [],
      statusOptions: [
        { text: 'Activo', value: 2 },
        { text: 'Desactivado', value: 4 },
        { text: 'Pendiente de pago', value: 5 }
      ],
      birthdateMenu: false,
      startDateMenu: false,
      graceDateMenu: false,
      rules: {
        required: value => !!value || 'Este campo es requerido',
        breedRequired: value => value !== undefined || 'Este campo es requerido'
      },
      loadingBreeds: false,
      loadingPlans: false,
      loadingCoupons: false,
      loading: false,
      membershipIsPlan: true,
      userContracts: [],
      loadingUserContracts: false,
      contractSelected: null,
      planSelected: null,
      showConfirmationDialog: false,
      snackbarSuccess: false,
      snackbarError: false,
      snackbarMessage: ''
    };
  },
  mounted() {
    this.fetchUser();
    this.loadUserContracts();
    this.loadAcquisitionChannels();
  },
  methods: {
    // Sugerencias para el combobox de canal de adquisición: valores ya
    // existentes en memberships (no hay catálogo). Si queda vacío, el
    // backend sigue calculando el canal automáticamente a partir del contrato.
    async loadAcquisitionChannels() {
      this.loadingAcquisitionChannels = true;
      try {
        const response = await MembershipService.getAcquisitionChannels();
        if (response.status === 'ok') {
          this.acquisitionChannelOptions = response.data;
        }
      } catch (err) {
        console.error('Error al obtener canales de adquisición:', err.message);
      } finally {
        this.loadingAcquisitionChannels = false;
      }
    },
    // Al elegir la fecha de inicio se autorellena la carencia con el mismo valor
    // (editable después por el usuario).
    onStartDateChange(date) {
      this.membership.startGraceDays = date;
      this.startDateMenu = false;
    },
    // Obtener planes disponibles
    /*async getPlans() {
      this.loadingPlans = true;
      try {
        if (!this.user || !this.user.type) {
          throw new Error('El tipo de usuario no está disponible');
        }

        const userType = this.user.type.toUpperCase();

        const response = await PlansService.getPlans(1, 100, true, userType);
        if (response.status === 'ok') {
          this.planOptions = response.data.plansV2.map(plan => ({
            text: plan.name,
            value: plan.id
          }));
        } else {
          console.error('Error al obtener los planes:', response.message);
          this.snackbarError = true;
          this.snackbarMessage = 'Error al obtener los planes';
        }
      } catch (err) {
        console.error('Error al obtener los planes:', err.message);
        this.snackbarError = true;
        this.snackbarMessage = 'Error al obtener los planes';
      } finally {
        this.loadingPlans = false;
      }
    },*/

    async getAvailablePlans() {
      try {
        this.loadingPlans = true;
        const response = await PlansService.getPlansByUserId(this.userId);
        if (response.status === 'ok') {
          this.planOptions = response.data.map(plan => ({
            text: `${plan.name} - ${plan.type.toUpperCase()} - ${plan.internalDescription}`,
            value: plan.id
          }));
        }
        this.loadingPlans = false;
      } catch (err) {
        console.error('Error al obtener los planes:', err.message);
        this.snackbarError = true;
        this.snackbarMessage = 'Error al obtener los planes';
      }
    },

    // Obtener cupones activos
    async getCoupons() {
      this.loadingCoupons = true;
      try {
        const response = await CouponsService.getActiveCoupons(this.user.country);
        if (response.status === 'ok') {
          this.couponOptions = response.data.map(coupon => ({
            text: coupon.name,
            value: coupon.code
          }));
        } else {
          console.error('Error al obtener los cupones:', response.message);
        }
      } catch (error) {
        console.error('Error al obtener los cupones:', error);
      } finally {
        this.loadingCoupons = false;
      }
    },
    // Obtener las razas según la especie seleccionada
    async getBreeds() {
      if (!this.pet.species) return;
      this.loadingBreeds = true;
      try {
        const response = await PetsService.getBreeds(this.pet.species);
        if (response.status === 'ok') {
          this.breedOptions = response.data.map(breed => ({
            text: breed.name,
            value: breed.id
          }));
          this.breedOptions.unshift({ text: 'Mestizo', value: null });
        } else {
          console.error('Error al obtener las razas:', response.message);
        }
      } catch (err) {
        console.error('Error al obtener las razas:', err.message);
      } finally {
        this.loadingBreeds = false;
      }
    },
    // Calcular la edad de la mascota
    getAge(birthdate) {
      const now = new Date();
      const birth = new Date(birthdate);

      let years = now.getFullYear() - birth.getFullYear();
      let months = now.getMonth() - birth.getMonth();

      if (months < 0) {
        years--;
        months += 12;
      }

      if (years > 0) {
        return { quantity: years, unit: 'Y' };
      } else {
        return { quantity: months, unit: 'M' };
      }
    },
    // Mostrar el diálogo de confirmación
    showConfirmation() {
      if (this.$refs.form.validate()) {
        this.showConfirmationDialog = true;
      }
    },
    // Confirmar y enviar el formulario
    confirmSubmit() {
      this.showConfirmationDialog = false;
      this.performSubmit();
    },
    async fetchUser() {
      try {
        const response = await UserService.getUserById(this.userId);
        if (response.status === 'ok') {
          this.user = response.data;
          // Llamo a getCoupons() y getPlans() después de obtener el usuario
          await this.getCoupons();
          //await this.getPlans();
          await this.getAvailablePlans();
        } else {
          console.error('Error al obtener los datos del usuario:', response.message);
        }
      } catch (error) {
        console.error('Error al obtener los datos del usuario:', error);
      } finally {
        //this.isLoading = false;
      }
    },
    // Enviar el formulario
    async performSubmit() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        try {
          // Calcular la edad de la mascota
          this.pet.age = this.getAge(this.pet.birthdate);

          // Preparar los datos de la mascota
          const petData = {
            name: this.pet.name,
            birthdate: this.pet.birthdate,
            species: this.pet.species,
            owner: this.userId,
            sex: this.pet.sex,
            breedId: this.pet.breed ? this.pet.breed : null,
            isPurebred: this.pet.breed ? true : false // Suponiendo que si tiene breedId, es de raza pura
          };

          // Preparar los datos de la membresía
          const membershipData = {
            startDate: this.membership.startDate,
            // Carencia por defecto = startDate cuando el admin no la edita
            startGraceDays:
              this.membership.startGraceDays || this.membership.startDate,
            planId: this.membership.plan,
            paymentInterval: this.membership.paymentMethod,
            status: this.membership.status,
            userId: this.userId,
            ucaId: this.membership.ucaId || null,
            // Opcional: si se deja vacío, el backend calcula el canal
            // automáticamente según el contrato asociado.
            acquisitionChannel: this.membership.acquisitionChannel || undefined
          };

          // Preparar el cuerpo de la solicitud
          const requestBody = {
            pet: petData,
            membership: membershipData,
            userId: this.userId,
            coupon: this.membership.coupon || null // Incluir el cupón si existe
          };

          // Llamar al endpoint del backend
          const response = await MembershipService.createPetAndMembership(requestBody);

          if (response.status === 'ok') {
            if (this.embedded) {
              this.$emit('saved');
              return;
            }
            this.snackbarSuccess = true;
            this.snackbarMessage = 'Mascota y membresía agregadas exitosamente';
            /*setTimeout(() => {
              this.$router.push({ name: 'Pets by User', params: { userId: this.userId } });
            }, 3000);*/
          } else {
            console.error('Error al agregar la mascota y membresía:', response.message);
            this.snackbarError = true;
            this.snackbarMessage =
              response.code === 'PLAN_NOT_ALLOWED_FOR_ACCESS'
                ? 'Este plan no está disponible para el acceso del usuario al contrato'
                : 'Error al agregar la mascota y membresía';
            console.log('soy response', response);
          }
        } catch (error) {
          console.error('Error en la solicitud:', error);
          this.snackbarError = true;
          this.snackbarMessage = 'Error en la solicitud';
          console.log('soy error', error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    onMembershipTypeChange() {
      this.membership.plan = null;
      this.membership.ucaId = null;
      this.membership.acquisitionChannel = null;
      this.contractSelected = null;
      this.planSelected = null;
    },
    async loadUserContracts() {
      this.loadingUserContracts = true;
      try {
        const res = await MembershipService.getUserContracts(this.userId);
        if (res.status === 'ok') {
          this.userContracts = res.data;
        }
      } catch (err) {
        console.error('Error al obtener contratos del usuario:', err.message);
      } finally {
        this.loadingUserContracts = false;
      }
    },
    onContractChange() {
      this.planSelected = null;
      this.membership.plan = null;
      this.membership.ucaId = null;
      this.membership.acquisitionChannel = null;
      this.loadAcquisitionChannelPreview();
    },
    // Precarga el canal de adquisición con el valor que calcularía el backend
    // para el contrato elegido (editable después por el admin).
    async loadAcquisitionChannelPreview() {
      if (!this.contractSelected) return;
      this.loadingAcquisitionChannelPreview = true;
      try {
        const response = await MembershipService.getAcquisitionChannelPreview(
          this.contractSelected.contratoId
        );
        if (response.status === 'ok') {
          this.membership.acquisitionChannel = response.data;
          // El combobox solo puede MOSTRAR un valor presente en :items — si el
          // contrato calcula un canal que ningún membership ha usado todavía
          // (empresa/broker nuevo), agregarlo para que se vea precargado.
          if (
            response.data &&
            !this.acquisitionChannelOptions.includes(response.data)
          ) {
            this.acquisitionChannelOptions = [
              ...this.acquisitionChannelOptions,
              response.data
            ];
          }
        }
      } catch (err) {
        console.error('Error al previsualizar el canal de adquisición:', err.message);
      } finally {
        this.loadingAcquisitionChannelPreview = false;
      }
    },
    onPlanChange() {
      if (!this.planSelected || !this.contractSelected) return;
      const { planId } = this.planSelected;
      const { ucas } = this.contractSelected;
      // Primero el acceso restringido a ese plan; si no hay, el acceso sin plan (specs/105).
      const access = ucas.find(u => u.planId === planId) || ucas.find(u => !u.planId);
      this.membership.plan = planId;
      this.membership.ucaId = access ? access.ucaId : null;
    },
    // Cancelar y volver atrás
    cancel() {
      if (this.embedded) {
        this.$emit('cancel');
        return;
      }
      this.$router.push({ name: 'Pets by User', params: { userId: this.userId } });
    }
  },
  computed: {
    // Planes del contrato que permiten los accesos del usuario: todos si alguno no tiene plan.
    allowedPlanes() {
      if (!this.contractSelected) return [];
      const { planes, ucas } = this.contractSelected;
      if (ucas.some(u => !u.planId)) return planes;
      return planes.filter(p => ucas.some(u => u.planId === p.planId));
    },
    // Obtener el texto del estado de la membresía
    getMembershipStatusText() {
      const statusValue = this.membership.status;
      const statusOption = this.statusOptions.find(option => option.value === statusValue);
      return statusOption ? statusOption.text : '';
    }
  }
};
</script>
